import { onboardingApi } from "@features/onboarding/utility-setup/api";
import { ApiEndpoints } from "@shared/api/api-endpoints";

import {
  authApiClient,
  cxApiClient,
  onboardingApiClient,
  bxApiClient,
  mxApiClient,
} from "@shared/api/clients/apiClientFactory";
import {
  CreateTemplateOperationsPayload,
  CreateTemplateOperationsResponse,
  MeterReaderListItem,
  UpdateMeterDispatchPayload,
  UpdateMeterDispatchResponse,
} from "./types";
import { promises } from "dns";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import { PAGE_SIZE } from "@shared/utils/constants";

export const mxApi = {
  getMeterRun: async (params: {
    remote_utility_id: string;
    meter_type: string;
    page: number;
    page_size: number;
    search?: string;
    ordering?: string;
    run_status?: string;
    month_year?: string;
    startDate?: string;
    endDate?: string;
    isCompleted?: boolean;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "meter-run",
      (query) => {
        query.push("remote_utility_id", params.remote_utility_id);
        query.push("meter_type", params.meter_type);
        query.push("page", params.page);
        query.push("limiit", PAGE_SIZE);

        if (params.month_year) {
          query.push("month_year", params.month_year);
        }
        if (params.run_status) {
          query.push("run_status", params.run_status);
        }
        if (params.ordering) {
          query.push("ordering", params.ordering);
        }
        if (params.isCompleted) {
          query.push("run_status", "3"); // backend filtering with choices where 2 is completed
        }
        if (params.startDate) {
          query.push("start_date", params.startDate);
        }
        if (params.endDate) {
          query.push("end_date", params.endDate);
        }
        if (params.search) {
          query.push("search_data", params.search);
        }
      }
    );

    const response = await mxApiClient.get(url);
    return response.data;
  },

  exportMeterRun: async (params: {
    remote_utility_id: string;
    meter_type: string;
    page?: number;
    page_size?: number;
    ordering?: string;
    run_status?: string;
    month_year?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "meter-run",
      (query) => {
        query.push("remote_utility_id", params.remote_utility_id);
        query.push("meter_type", params.meter_type);

        if (params.page) query.push("page", params.page);
        if (params.page_size) query.push("page_size", params.page_size);

        if (params.month_year) {
          query.push("month_year", params.month_year);
        }
        if (params.run_status) {
          query.push("run_status", params.run_status);
        }
        if (params.ordering) {
          query.push("ordering", params.ordering);
        }

        query.push("export_format", "csv");
      }
    );

    const response = await mxApiClient.get(url);

    return response.data;
  },

  getMeterRunSummary: async (params: {
    remote_utility_id: string;
    meter_type: string;
  }): Promise<any> => {
    const response = await mxApiClient.get(
      `meter-run/summary/?remote_utility_id=${params.remote_utility_id}&meter_type=${params.meter_type}`
    );
    return response.data;
  },

  getMeterRunDetail: async (params: {
    meter_run_id: string;
    remote_utility_id: string;
    meter_type: string;
  }): Promise<any> => {
    const response = await mxApiClient.get(
      `meter-run/detail/${params.meter_run_id}/?remote_utlity_id=${params.remote_utility_id}&meter_type=${params.meter_type}`
    );
    return response.data;
  },

  getMeterDispatchDetail: async (params: {
    meter_run_id: string;
    remote_utility_id: string;
    page: number;
    origin: string;
    meter_reader_id?: any;
    run_status?: any;
    search?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "meter-dispatch/detail",
      (query) => {
        query.push("run_id", params.meter_run_id);
        query.push("remote_utility_id", params.remote_utility_id);
        query.push("page", params.page);
        query.push("limit", PAGE_SIZE);
        query.push("origin", "Web");

        if (params.meter_reader_id) {
          query.push("meter_reader_id", params.meter_reader_id);
        }
        if (
          params.run_status !== null &&
          params.run_status !== undefined &&
          params.run_status !== ""
        ) {
          query.push("status", params.run_status);
        }
        if (params.search) {
          query.push("search_data", params.search);
        }
      }
    );
    const response = await mxApiClient.get(url);
    return response.data;
  },

  getAssignConsumer: async (params: {
    remote_utility_id: string;
    isActive: string;
    page: number;
    search?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "meter-reader",
      (query) => {
        query.push("remote_utility_id", params.remote_utility_id);
        query.push("is_active", params.isActive);
        query.push("page", params.page);
        query.push("limit", PAGE_SIZE);
        if (params.search) {
          query.push("search_data", params.search);
        }
      }
    );
    const response = await mxApiClient.get(url);
    return response.data;
  },
  updateMeterDispatchDetail: async (
    dispatchId: string,
    payload: UpdateMeterDispatchPayload
  ): Promise<UpdateMeterDispatchResponse> => {
    const response = await mxApiClient.patch(
      `meter-dispatch/detail/${dispatchId}/`,
      payload
    );
    return response.data;
  },
  downloadTemplateOperations: async (params: {
    remote_utility_id: string;
    run_id: string;
  }): Promise<Blob> => {
    const response = await mxApiClient.get(
      `template-operations/?remote_utility_id=${params.remote_utility_id}&run_id=${params.run_id}`,
      {
        responseType: "blob", // Important for file downloads
      }
    );
    return response.data;
  },
  createTemplateOperations: async (
    payload: CreateTemplateOperationsPayload
  ): Promise<CreateTemplateOperationsResponse> => {
    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("remote_utility_id", payload.remote_utility_id);

    const response = await mxApiClient.post(`template-operations/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },
  getRunStatusChoices: async (): Promise<any> => {
    const response = await mxApiClient.get(`choices/run_status/`);
    return response.data;
  },
  getManualRunStatusChoices: async (): Promise<any> => {
    const response = await mxApiClient.get(`choices/manual_run_status/`);
    return response.data;
  },
  getDispatchStatus: async (): Promise<any> => {
    const response = await mxApiClient.get(`choices/new_dispatch_status/`);
    return response.data;
  },

  getMeterRead: async (params: {
    remote_utility_id: string;
    meter_type: string;
    page: any;
    run_status: any;
    month_year: any;
  }): Promise<any> => {
    const response = await mxApiClient.get(
      `meter-run-list/?remote_utility_id=${params.remote_utility_id}&meter_type=${params.meter_type}&page=${params.page}&run_status=${params.run_status}&month_year=${params.month_year}`
    );
    return response.data;
  },
};

export const getMeterReaderList = async (
  remoteUtilityId: string
): Promise<MeterReaderListItem[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reader/list",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MeterReaderListItem[]>(response.data.result);
};

export const getReadingsList = async (
  remoteUtilityId: string,
  meterRunId: string,
  status?: string,
  type?: string,
  search?: string,
  page?: number
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reading",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_run", meterRunId);
      query.push("page", page);
      query.push("limit", PAGE_SIZE);
      if (status !== null && status !== undefined) query.push("status", status);
      if (type !== null && type !== undefined) query.push("type", type);
      if (search) query.push("search_data", search);
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<any>(response.data);
};

export const getValidationCompletionDetail = async (
  remoteUtilityId: string,
  meterRunId
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "mark-cycle-complete",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_run_id", meterRunId);
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<any>(response.data.result);
};

export const runValidations = async (payload: any): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "run-auto-validation"
  );
  const response = await mxApiClient.post(url, payload);
  return response.data;
};
export const markCycleAsComplete = async (payload: any): Promise<any> => {
  const response = await mxApiClient.put(`mark-cycle-complete`, payload);
  return response.data;
};

export const getPhotoMeterRules = async (
  ruleType: number,
  utilityId: string,
  meterCycleId?: number
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `rules`,
    (query) => {
      query.push("rule_type", ruleType);
      query.push("remote_utility_id", utilityId);
      if (meterCycleId) {
        query.push("meter_cycle_id", meterCycleId);
      }
    }
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};
